import { useState } from 'react';
import { User, Mail, Phone, RefreshCw, Save, MapPin, Settings, Bell, Sparkles, BookOpen, KeyRound, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { userApi } from '@/services/api';
import { useToast } from '@/components/ui/toast';
import { SubscriptionStatusCard } from '@/components/subscription/SubscriptionStatusCard';
import { BaseAirportModal } from '@/components/dashboard/BaseAirportModal';
import { Link } from 'react-router-dom';

/** 内嵌 OTP 输入弹窗（修改邮箱时使用） */
function EmailOtpDialog({
  open,
  currentEmail,
  onClose,
  onVerified,
}: {
  open: boolean;
  currentEmail: string;
  onClose: () => void;
  onVerified: (code: string) => void;
}) {
  const [code, setCode] = useState('');
  const { addToast } = useToast();

  if (!open) return null;

  const handleConfirm = () => {
    if (code.length !== 6) {
      addToast({ title: '请输入完整的 6 位验证码', variant: 'warning' });
      return;
    }
    onVerified(code);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          <h3 className="text-base font-semibold text-gray-900">验证当前邮箱</h3>
        </div>
        <p className="text-sm text-gray-500">
          验证码已发送至 <span className="font-medium">{currentEmail}</span>，用于确认邮箱修改。
        </p>
        <div className="space-y-2">
          <Label htmlFor="emailOtp">6 位验证码</Label>
          <Input
            id="emailOtp"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="tracking-widest text-center text-lg"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>取消</Button>
          <Button className="flex-1" onClick={handleConfirm} disabled={code.length !== 6}>确认</Button>
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { user, updateUser } = useAuthStore();
  const { addToast } = useToast();

  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showBaseModal, setShowBaseModal] = useState(false);

  // 邮箱修改 OTP
  const [emailOtpOpen, setEmailOtpOpen] = useState(false);
  const [pendingEmailChangeCode, setPendingEmailChangeCode] = useState<string | null>(null);

  // 修改密码
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [isChangingPwd, setIsChangingPwd] = useState(false);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const doSaveProfile = async (emailChangeCode?: string) => {
    setIsSavingProfile(true);
    try {
      const payload: Record<string, string> = {
        displayName: profileData.displayName,
        email: profileData.email,
        phone: profileData.phone,
      };
      if (emailChangeCode) payload.emailChangeCode = emailChangeCode;

      await userApi.updateProfile(payload as any);
      updateUser(profileData);
      addToast({ title: '保存成功', description: '个人信息已更新', variant: 'success' });
      setPendingEmailChangeCode(null);
    } catch (error: any) {
      const data = error.response?.data;
      const code = data?.code;

      // 邮箱修改需要 OTP
      if (code === 'EMAIL_CHANGE_OTP_SENT') {
        setEmailOtpOpen(true);
        return;
      }
      if (code === 'INVALID_VERIFICATION_CODE') {
        addToast({ title: '验证码错误', description: '请检查后重试', variant: 'error' });
        return;
      }

      addToast({ title: '保存失败', description: data?.message || '请稍后重试', variant: 'error' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const saveProfile = async () => {
    const emailChanged = profileData.email !== (user?.email || '');
    if (emailChanged && !pendingEmailChangeCode) {
      // 首次尝试：不带 code，让后端发送 OTP
      await doSaveProfile();
    } else if (emailChanged && pendingEmailChangeCode) {
      await doSaveProfile(pendingEmailChangeCode);
    } else {
      await doSaveProfile();
    }
  };

  const handleEmailOtpVerified = (code: string) => {
    setPendingEmailChangeCode(code);
    setEmailOtpOpen(false);
    // 带 code 重新保存
    doSaveProfile(code);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwdCurrent || !pwdNew) {
      addToast({ title: '请填写完整', variant: 'warning' });
      return;
    }
    if (pwdNew !== pwdConfirm) {
      addToast({ title: '两次新密码输入不一致', variant: 'warning' });
      return;
    }
    setIsChangingPwd(true);
    try {
      await userApi.changePassword({ currentPassword: pwdCurrent, newPassword: pwdNew });
      addToast({ title: '密码已修改', description: '请使用新密码重新登录', variant: 'success' });
      setPwdCurrent('');
      setPwdNew('');
      setPwdConfirm('');
    } catch (error: any) {
      const data = error.response?.data;
      addToast({ title: '修改失败', description: data?.message || '请稍后重试', variant: 'error' });
    } finally {
      setIsChangingPwd(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">设置</h1>

      {/* Subscription */}
      <SubscriptionStatusCard />

      {user?.isStaff && (
        <Card>
          <CardContent className="py-4">
            <Link to="/admin/subscription">
              <Button variant="outline" className="w-full">
                <Settings className="w-4 h-4 mr-2" />
                管理订阅
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* AI & Push & Vault Settings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-4">
            <Link to="/settings/ai">
              <Button variant="outline" className="w-full">
                <Sparkles className="w-4 h-4 mr-2" />AI 设置
              </Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <Link to="/settings/push">
              <Button variant="outline" className="w-full">
                <Bell className="w-4 h-4 mr-2" />Bark 推送设置
              </Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <Link to="/settings/vault">
              <Button variant="outline" className="w-full">
                <BookOpen className="w-4 h-4 mr-2" />Vault 同步设置
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />个人信息
          </CardTitle>
          <CardDescription>管理您的基本资料</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">昵称</Label>
              <Input
                id="displayName"
                name="displayName"
                value={profileData.displayName}
                onChange={handleProfileChange}
                placeholder="请输入昵称"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">邮箱</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  placeholder="请输入邮箱"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">手机号</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                  placeholder="请输入手机号"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseAirport">基地机场</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="baseAirport"
                    value={user?.baseAirport || ''}
                    placeholder="未设置"
                    readOnly
                    className="pl-10 bg-gray-50"
                  />
                </div>
                <Button type="button" variant="outline" onClick={() => setShowBaseModal(true)}>
                  {user?.baseAirport ? '修改' : '设置'}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={isSavingProfile}>
              {isSavingProfile ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              保存
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />修改密码
          </CardTitle>
          <CardDescription>修改登录密码，完成后需重新登录</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">当前密码</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={pwdCurrent}
                  onChange={(e) => setPwdCurrent(e.target.value)}
                  placeholder="请输入当前密码"
                  disabled={isChangingPwd}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">新密码</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={pwdNew}
                  onChange={(e) => setPwdNew(e.target.value)}
                  placeholder="请输入新密码"
                  disabled={isChangingPwd}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwdConfirm">确认新密码</Label>
                <Input
                  id="pwdConfirm"
                  type="password"
                  value={pwdConfirm}
                  onChange={(e) => setPwdConfirm(e.target.value)}
                  placeholder="请再次输入新密码"
                  disabled={isChangingPwd}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={isChangingPwd || !pwdCurrent || !pwdNew}>
                {isChangingPwd ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                修改密码
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <EmailOtpDialog
        open={emailOtpOpen}
        currentEmail={user?.email || ''}
        onClose={() => setEmailOtpOpen(false)}
        onVerified={handleEmailOtpVerified}
      />

      <BaseAirportModal isOpen={showBaseModal} onClose={() => setShowBaseModal(false)} />
    </div>
  );
}
